import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Tbody } from '@root-gipro/modules/userProjects/components/Project/UncTable/Tbody'
import { gridTemplateColumns, theadData } from '@root-gipro/modules/userProjects/models/consts/unc-thead.const'
import { IUnc } from '@root-gipro/modules/userProjects/models/interfaces/user-projects.model'
import { Thead } from '@root-gipro/shared/Table/components/Thead'
import styles from '@root-gipro/shared/Table/styles/Table.module.scss'
import {
	sortUncCellCode,
	sortUncCost,
	sortUncCount,
	sortUncParameters,
	sortUncRegionFactor,
	sortUncTechnicalSolutions,
	sortUncTotalCost,
	sortUncUnit,
	sortUncVoltage,
} from '@root-gipro/utils/helpers/handleSorting'

import {
	filteredTotalUncCost,
	filteredUncCellCode,
	filteredUncCost,
	filteredUncCount,
	filteredUncParameters,
	filteredUncRegionFactor,
	filteredUncTechnicalSolutions,
	filteredUncUnit,
	filteredUncVoltage,
} from '@root-gipro/utils/helpers/handleFiltered'
import TableTabs from './TableTabs'

import { Backdrop, CircularProgress, LinearProgress, Menu, MenuItem } from '@material-ui/core'
import { ModalContext } from '@root-gipro/context/modal-context'
import { cellTags } from '@root-gipro/modules/userProjects/models/consts/unc-cell-tags.const'
import { setActiveUncs } from '@root-gipro/modules/userProjects/store/actions'
import { fetchData } from '@root-gipro/store/api'
import { IState } from '@root-gipro/store/interfaces'
import { useDispatch, useSelector } from 'react-redux'

interface TableContainerProps {
	uncs: IUnc[]
	sitePreparation: boolean
}

type filterFieldsProp = { [key: string]: any }

const TableContainer: FC<TableContainerProps> = ({ uncs, sitePreparation }) => {
	const { uncCheckedTable, showLoader, activeUncs, projectObj, originalObj } = useSelector(
		(state: IState) => state.userProjects
	)
	const { regions } = useSelector((state: IState) => state.public)
	const [uncsFiltered, setUncsFiltered] = useState<IUnc[] | []>([])
	const [filterFields, setFilterFields] = useState<filterFieldsProp>({})
	const [regionMenuAnchor, setRegionMenuAnchor] = useState<null | HTMLElement>(null)
	const [isUpdatingRegions, setIsUpdatingRegions] = useState(false)
	const [updateProgress, setUpdateProgress] = useState({ current: 0, total: 0 })
	const [updateError, setUpdateError] = useState<string | null>(null)
	const [regionCache, setRegionCache] = useState<Map<string, any>>(new Map())

	// Состояния для удаления расценок
	const [isDeletingRates, setIsDeletingRates] = useState(false)
	const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 })
	const [deleteError, setDeleteError] = useState<string | null>(null)
	const dispatch = useDispatch()

	const theadParams: { [key: string]: any } = useMemo(() => {
		return {
			gridTemplateColumns,
			theadData,
		}
	}, [])

	const updateHandleSorting = (sortField: string, sortOrder: string) => {
		let sorted: [] | IUnc[] = []
		if (sortField === '№') {
			sorted = sortUncCellCode('id', sortOrder, uncs)
		} else if (sortField === 'Расценка') {
			sorted = sortUncCellCode(sortField, sortOrder, uncsFiltered)
		} else if (sortField === 'Техническое решение') {
			sorted = sortUncTechnicalSolutions(sortField, sortOrder, uncsFiltered)
		} else if (sortField === 'Кол-во') {
			sorted = sortUncCount(sortField, sortOrder, uncsFiltered)
		} else if (sortField === 'Ед.изм.') {
			sorted = sortUncUnit(sortField, sortOrder, uncsFiltered)
		} else if (sortField === 'Параметры') {
			sorted = sortUncParameters(sortField, sortOrder, uncsFiltered)
		} else if (sortField === 'Напр., кВ') {
			sorted = sortUncVoltage(sortField, sortOrder, uncsFiltered)
		} else if (sortField === 'Укр. норматив цены') {
			sorted = sortUncCost(sortField, sortOrder, uncsFiltered)
		} else if (sortField === 'Региональный коэф.') {
			sorted = sortUncRegionFactor(sortField, sortOrder, uncsFiltered)
		} else if (sortField === 'Величина затрат') {
			sorted = sortUncTotalCost(sortField, sortOrder, uncsFiltered)
		}
		setUncsFiltered(sorted)
	}

	const updateHandleFiltered = (inputFiled: string, value: any) => {
		if (inputFiled === 'Расценка' && value !== undefined) {
			if (value === '') {
				setFilterFields(prev => ({ ...prev, Расценка: null }))
			} else {
				setFilterFields(prev => ({ ...prev, Расценка: value }))
			}
		}
		if (inputFiled === 'Техническое решение' && value !== undefined) {
			if (value === '') {
				setFilterFields(prev => ({ ...prev, 'Техническое решение': null }))
			} else {
				setFilterFields(prev => ({ ...prev, 'Техническое решение': value }))
			}
		}
		if (inputFiled === 'Кол-во' && value !== undefined) {
			if (value === '') {
				setFilterFields(prev => ({ ...prev, 'Кол-во': null }))
			} else {
				setFilterFields(prev => ({ ...prev, 'Кол-во': value }))
			}
		}
		if (inputFiled === 'Ед.изм.' && value !== undefined) {
			if (value === '') {
				setFilterFields(prev => ({ ...prev, 'Ед.изм.': null }))
			} else {
				setFilterFields(prev => ({ ...prev, 'Ед.изм.': value }))
			}
		}
		if (inputFiled === 'Параметры' && value !== undefined) {
			if (value === '') {
				setFilterFields(prev => ({ ...prev, Параметры: null }))
			} else {
				setFilterFields(prev => ({ ...prev, Параметры: value }))
			}
		}
		if (inputFiled === 'Напр., кВ' && value !== undefined) {
			if (value === '') {
				setFilterFields(prev => ({ ...prev, 'Напр., кВ': null }))
			} else {
				setFilterFields(prev => ({ ...prev, 'Напр., кВ': value }))
			}
		}
		if (inputFiled === 'Укр. норматив цены' && value !== undefined) {
			if (value === '') {
				setFilterFields(prev => ({ ...prev, 'Укр. норматив цены': null }))
			} else {
				setFilterFields(prev => ({ ...prev, 'Укр. норматив цены': value }))
			}
		}
		if (inputFiled === 'Региональный коэф.' && value !== undefined) {
			if (value === '') {
				setFilterFields(prev => ({ ...prev, 'Региональный коэф.': null }))
			} else {
				setFilterFields(prev => ({ ...prev, 'Региональный коэф.': value }))
			}
		}
		if (inputFiled === 'Величина затрат' && value !== undefined) {
			if (value === '') {
				setFilterFields(prev => ({ ...prev, 'Величина затрат': null }))
			} else {
				setFilterFields(prev => ({ ...prev, 'Величина затрат': value }))
			}
		}
	}

	const filtered = () => {
		let filtered: [] | IUnc[] = uncs.length ? uncs : []

		if (typeof filterFields['Расценка'] !== 'undefined') {
			const query = filterFields['Расценка']
			filtered = filteredUncCellCode(query, filtered)
		}
		if (typeof filterFields['Техническое решение'] !== 'undefined') {
			const query = filterFields['Техническое решение']
			filtered = filteredUncTechnicalSolutions(query, filtered)
		}
		if (typeof filterFields['Кол-во'] !== 'undefined') {
			const query = filterFields['Кол-во']
			filtered = filteredUncCount(query, filtered)
		}

		if (typeof filterFields['Ед.изм.'] !== 'undefined') {
			const query = filterFields['Ед.изм.']
			filtered = filteredUncUnit(query, filtered)
		}
		if (typeof filterFields['Параметры'] !== 'undefined') {
			const query = filterFields['Параметры']
			filtered = filteredUncParameters(query, filtered)
		}
		if (typeof filterFields['Напр., кВ'] !== 'undefined') {
			const query = filterFields['Напр., кВ']
			filtered = filteredUncVoltage(query, filtered)
		}
		if (typeof filterFields['Укр. норматив цены'] !== 'undefined') {
			const query = filterFields['Укр. норматив цены']
			filtered = filteredUncCost(query, filtered)
		}
		if (typeof filterFields['Региональный коэф.'] !== 'undefined') {
			const query = filterFields['Региональный коэф.']
			filtered = filteredUncRegionFactor(query, filtered)
		}
		if (typeof filterFields['Величина затрат'] !== 'undefined') {
			const query = filterFields['Величина затрат']
			filtered = filteredTotalUncCost(query, filtered)
		}
		setUncsFiltered(filtered)
	}

	const { setAnchorEl } = useContext(ModalContext)

	const openWindow = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			setAnchorEl!(e.currentTarget)
		},
		[setAnchorEl]
	)

	const handleRegionSelect = useCallback(
		async (regionId: number, zoneId?: string) => {
			// Применяем выбранный регион ко всем выбранным UNC
			if (activeUncs?.length === 0 || isUpdatingRegions) return

			setIsUpdatingRegions(true)
			setUpdateError(null)

			// Фильтруем UNC, которые действительно нужно обновить
			const uncsToUpdate = activeUncs?.filter(unc => {
				if (zoneId) {
					return unc.region_zone_id !== zoneId
				} else {
					return !(Number(unc.regionId) === regionId && !unc.region_zone_id)
				}
			})

			setUpdateProgress({ current: 0, total: uncsToUpdate.length })

			let successCount = 0
			let errorCount = 0

			try {
				// Последовательная обработка с реальным ожиданием ответа API
				for (let i = 0; i < uncsToUpdate.length; i++) {
					const unc = uncsToUpdate[i]

					// Обновляем прогресс
					setUpdateProgress({ current: i + 1, total: uncsToUpdate.length })

					try {
						// Проверяем кэш
						const cacheKey = `${unc.id}-${regionId}-${zoneId || 'no-zone'}`
						if (regionCache.has(cacheKey)) {
							successCount++
						} else {
							// Прямой API вызов с ожиданием ответа
							const uncElem = await fetchData(
								`/user-uncs/${unc.id}?with=regionFactor`,
								(res: any) => res.userUnc,
								'PATCH',
								JSON.stringify({ regionId, region_zone_id: zoneId })
							)

							if (uncElem) {
								// Обновляем стор с новыми данными
								dispatch({ type: 'FETCH_USER_PROJECT', id: uncElem.userProjectId })
								dispatch({ type: 'FETCH_UNCS', id: uncElem.userProjectId })

								// Сохраняем в кэш
								setRegionCache(prev => new Map(prev.set(cacheKey, { regionId, zoneId, timestamp: Date.now() })))
								successCount++
							} else {
								throw new Error('Пустой ответ от сервера')
							}
						}

						// Задержка между запросами для предотвращения перегрузки
						if (i < uncsToUpdate.length - 1) {
							await new Promise(resolve => setTimeout(resolve, 200)) // 200ms задержка
						}
					} catch (error) {
						errorCount++
					

						// Прерываем цепочку при ошибке
						const errorMessage = `Ошибка при обновлении расценки ${unc.id}. Обновлено: ${successCount}, ошибок: ${
							errorCount + 1
						}`
						setUpdateError(errorMessage)
						throw new Error(errorMessage)
					}
				}
			} catch (error) {
			
				setUpdateError(error instanceof Error ? error.message : 'Неизвестная ошибка')
			} finally {
				setIsUpdatingRegions(false)
				setUpdateProgress({ current: 0, total: 0 })

				// Очищаем ошибку через 5 секунд
				if (updateError) {
					setTimeout(() => setUpdateError(null), 5000)
				}
			}
		},
		[activeUncs, dispatch, isUpdatingRegions, regionCache, updateError]
	)

	// Состояние для зональных регионов
	const [allowedZoneIds, setAllowedZoneIds] = useState<number[]>([])

	// Функция для получения названий региональных зон (скопирована из Cells)
	function getRegionZoneNames(zoneIds: string | string[] | undefined): string {
		if (!zoneIds) return ''

		const idsArray = Array.isArray(zoneIds) ? zoneIds : [zoneIds]
		const names: string[] = []

		for (const region of regions) {
			for (const zone of region.region_zones || []) {
				if (idsArray.includes(zone.id)) {
					names.push(zone.name)
				}
			}
		}

		return names.join(', ')
	}

	// Функция для определения regionId по zoneId
	function getRegionIdByZoneId(zoneId: string): string | null {
		for (const region of regions) {
			if (region.region_zones?.some((zone: any) => zone.id === zoneId)) {
				return region.id
			}
		}
		return null
	}

	// Получаем список доступных регионов (логика как в RegionTag)
	const availableRegions = useMemo(() => {
		return regions?.filter((elem: any) => originalObj.regionIds?.includes(elem.id)) || []
	}, [regions, originalObj])

	// Устанавливаем allowedZoneIds при изменении projectObj
	useEffect(() => {
		if (projectObj?.regionZoneIds) {
			setAllowedZoneIds(projectObj.regionZoneIds)
		}
	}, [projectObj])

	// Функция очистки устаревшего кэша (старше 5 минут)
	const cleanupCache = useCallback(() => {
		const now = Date.now()
		const fiveMinutes = 5 * 60 * 1000

		setRegionCache(prev => {
			const newCache = new Map()
			prev.forEach((value, key) => {
				if (now - value.timestamp < fiveMinutes) {
					newCache.set(key, value)
				}
			})
			return newCache
		})
	}, [])

	// Очистка кэша при изменении активных расценок
	useEffect(() => {
		cleanupCache()
	}, [activeUncs, cleanupCache])

	// Обработчик для открытия меню выбора региона
	const handleOpenRegionMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (activeUncs?.length < 2 || isUpdatingRegions) return
			setRegionMenuAnchor(event.currentTarget)
		},
		[activeUncs, isUpdatingRegions]
	)

	// Обработчик для закрытия меню
	const handleCloseRegionMenu = useCallback(() => {
		setRegionMenuAnchor(null)
	}, [])

	// Обработчик для выбора региона из меню
	const handleSelectRegionFromMenu = useCallback(
		(regionId: number) => {
			handleCloseRegionMenu() // Закрываем меню сразу
			handleRegionSelect(regionId)
		},
		[handleRegionSelect, handleCloseRegionMenu]
	)

	// Обработчик для выбора зонального региона
	const handleSelectRegionZoneFromMenu = useCallback(
		(zoneId: string) => {
			const regionId = getRegionIdByZoneId(zoneId)
			if (regionId) {
				handleCloseRegionMenu() // Закрываем меню сразу
				handleRegionSelect(Number(regionId), zoneId)
			}
		},
		[handleRegionSelect, handleCloseRegionMenu]
	)

	// Функция повторной попытки обновления региона
	const handleRetryRegionUpdate = useCallback(() => {
		setUpdateError(null)
		// Повторяем последний выбор региона
		if (allowedZoneIds.length > 0) {
			// Если есть зональные регионы, выбираем первую зону
			for (const region of regions) {
				const firstZone = region.region_zones?.find((zone: { id: string; name: string }) =>
					allowedZoneIds.includes(Number(zone.id))
				)
				if (firstZone) {
					handleSelectRegionZoneFromMenu(firstZone.id)
					break
				}
			}
		} else if (availableRegions.length > 0) {
			// Иначе выбираем первый доступный регион
			const lastRegion = availableRegions[0]
			handleRegionSelect(Number(lastRegion.id))
		}
	}, [allowedZoneIds, availableRegions, regions, handleRegionSelect, handleSelectRegionZoneFromMenu])

	// Обработчик для кнопки "Удалить расценки"
	const handleDeleteRates = useCallback(async () => {
		if (activeUncs?.length < 2 || isDeletingRates) return

		setIsDeletingRates(true)
		setDeleteError(null)
		setDeleteProgress({ current: 0, total: activeUncs?.length })

		let successCount = 0
		let errorCount = 0

		try {
			// Последовательное удаление с прогрессом
			for (let i = 0; i < activeUncs?.length; i++) {
				const unc = activeUncs[i]

				// Обновляем прогресс
				setDeleteProgress({ current: i + 1, total: activeUncs?.length })

				try {
					// Прямой API вызов для удаления
					await fetchData(`/user-uncs/${unc.id}`, (res: any) => res, 'DELETE')
					successCount++

					// Задержка между запросами
					if (i < activeUncs?.length - 1) {
						await new Promise(resolve => setTimeout(resolve, 150)) // 150ms задержка
					}
				} catch (error) {
					errorCount++
				

					// Прерываем цепочку при ошибке
					const errorMessage = `Ошибка при удалении расценки ${unc.id}. Удалено: ${successCount}, ошибок: ${
						errorCount + 1
					}`
					setDeleteError(errorMessage)
					throw new Error(errorMessage)
				}
			}

			// Обновляем проект после успешного удаления
			if (successCount > 0 && activeUncs?.length > 0) {
				dispatch({ type: 'FETCH_USER_PROJECT', id: activeUncs[0].userProjectId })
				dispatch({ type: 'FETCH_UNCS', id: activeUncs[0].userProjectId })
			}

			// Сбрасываем выделение
			dispatch(setActiveUncs([]))
		} catch (error) {
		
			setDeleteError(error instanceof Error ? error.message : 'Неизвестная ошибка')
		} finally {
			setIsDeletingRates(false)
			setDeleteProgress({ current: 0, total: 0 })

			// Очищаем ошибку через 5 секунд
			if (deleteError) {
				setTimeout(() => setDeleteError(null), 5000)
			}
		}
	}, [activeUncs, dispatch, isDeletingRates, deleteError])

	useEffect(() => {
		setUncsFiltered(uncs)
	}, [uncs])

	useEffect(() => {
		filtered()
	}, [filterFields])

	const hasC1Code = (activeUncs: Array<{ uncMain?: { code?: string } }>): boolean => {
		return activeUncs.some(item => item.uncMain?.code === 'С1')
	}

	return (
		<>
			{/* CSS анимации для оверлея */}
			<style>
				{`
					@keyframes pulse {
						0% { opacity: 1; transform: scale(1); }
						50% { opacity: 0.7; transform: scale(1.05); }
						100% { opacity: 1; transform: scale(1); }
					}
					@keyframes fadeInUp {
						from { opacity: 0; transform: translateY(20px); }
						to { opacity: 1; transform: translateY(0); }
					}
					.loading-overlay {
						animation: fadeInUp 0.3s ease-out;
					}
					.pulse-animation {
						animation: pulse 2s infinite;
					}
				`}
			</style>
			<div className={styles.container}>
				{sitePreparation ? (
					<div style={{ textAlign: 'end', marginBottom: 15 }}>
						<span
							className={`link ${
								activeUncs?.length < 2 || isUpdatingRegions || isDeletingRates ? 'link-disabled' : 'link-blue'
							} ml-8`}
							style={{ cursor: activeUncs?.length < 2 || isUpdatingRegions || isDeletingRates ? 'default' : 'pointer' }}
							onClick={
								activeUncs?.length >= 2 && !isUpdatingRegions && !isDeletingRates ? handleOpenRegionMenu : undefined
							}
						>
							{isUpdatingRegions
								? `Обновление регионов... (${updateProgress.current}/${updateProgress.total})`
								: 'Выбрать регион'}
						</span>
						<span
							className={`link ${
								activeUncs?.length < 2 || isUpdatingRegions || isDeletingRates ? 'link-disabled' : 'link-blue'
							} ml-8`}
							style={{ cursor: activeUncs?.length < 2 || isUpdatingRegions || isDeletingRates ? 'default' : 'pointer' }}
							onClick={activeUncs?.length >= 2 && !isUpdatingRegions && !isDeletingRates ? handleDeleteRates : undefined}
						>
							{isDeletingRates ? `Удаление... (${deleteProgress.current}/${deleteProgress.total})` : 'Удалить расценки'}
						</span>
						<span
							data-popup={cellTags[1]}
							style={{ cursor: hasC1Code(activeUncs) ? 'pointer' : 'default' }}
							onClick={hasC1Code(activeUncs) ? openWindow : undefined}
							className={`link ${hasC1Code(activeUncs) ? 'link-blue' : 'link-disabled'} ml-8`}
						>
							Подготовка территории
						</span>
					</div>
				) : null}

				{/* Отображение ошибок обновления регионов */}
				{updateError && (
					<div
						style={{
							marginBottom: 15,
							padding: '8px 16px',
							backgroundColor: '#ffebee',
							borderRadius: '4px',
							border: '1px solid #f44336',
						}}
					>
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<span style={{ fontSize: '14px', color: '#d32f2f' }}>❌ {updateError}</span>
							<div style={{ display: 'flex', gap: '8px' }}>
								<button
									onClick={handleRetryRegionUpdate}
									style={{
										background: '#d32f2f',
										border: 'none',
										color: 'white',
										cursor: 'pointer',
										fontSize: '12px',
										padding: '4px 8px',
										borderRadius: '3px',
									}}
								>
									Повторить
								</button>
								<button
									onClick={() => setUpdateError(null)}
									style={{
										background: 'none',
										border: 'none',
										color: '#d32f2f',
										cursor: 'pointer',
										fontSize: '16px',
									}}
								>
									✕
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Отображение ошибок удаления расценок */}
				{deleteError && (
					<div
						style={{
							marginBottom: 15,
							padding: '8px 16px',
							backgroundColor: '#ffebee',
							borderRadius: '4px',
							border: '1px solid #f44336',
						}}
					>
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<span style={{ fontSize: '14px', color: '#d32f2f' }}>❌ {deleteError}</span>
							<div style={{ display: 'flex', gap: '8px' }}>
								<button
									onClick={handleDeleteRates}
									style={{
										background: '#d32f2f',
										border: 'none',
										color: 'white',
										cursor: 'pointer',
										fontSize: '12px',
										padding: '4px 8px',
										borderRadius: '3px',
									}}
								>
									Повторить
								</button>
								<button
									onClick={() => setDeleteError(null)}
									style={{
										background: 'none',
										border: 'none',
										color: '#d32f2f',
										cursor: 'pointer',
										fontSize: '16px',
									}}
								>
									✕
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Выпадающее меню для выбора региона */}
				<Menu
					anchorEl={regionMenuAnchor}
					keepMounted
					open={Boolean(regionMenuAnchor) && !isUpdatingRegions}
					onClose={handleCloseRegionMenu}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
				>
					{allowedZoneIds.length
						? regions.map(region => (
								<div key={region.id}>
									{region.region_zones
										?.filter((zone: { id: string; name: string }) => allowedZoneIds.includes(Number(zone.id)))
										?.map((zone: { id: string; name: string }) => (
											<MenuItem
												key={zone.id}
												onClick={() => handleSelectRegionZoneFromMenu(zone.id)}
												disabled={isUpdatingRegions}
											>
												{zone.name}
											</MenuItem>
										))}
								</div>
						  ))
						: availableRegions.map(region => (
								<MenuItem
									key={region.id}
									onClick={() => handleSelectRegionFromMenu(Number(region.id))}
									disabled={isUpdatingRegions}
								>
									{region.name}
								</MenuItem>
						  ))}
				</Menu>
				<TableTabs uncs={uncs} uncCheckedTable={uncCheckedTable} />

				<div className={styles.table}>
					<Thead
						gridTemplateColumns={theadParams['gridTemplateColumns']}
						theadData={theadParams['theadData']}
						updateHandleSorting={updateHandleSorting}
						updateHandleChange={updateHandleFiltered}
					/>

					<div className={styles.container_body}>
						<div>{showLoader && <LinearProgress />}</div>
						<Tbody uncs={uncs} uncsFiltered={uncsFiltered} checkedTable={uncCheckedTable} />
					</div>
				</div>
			</div>

			{/* Оверлей для блокировки интерфейса во время обновления регионов */}
			<Backdrop
				open={isUpdatingRegions}
				className='loading-overlay'
				style={{
					zIndex: 9999,
					color: '#fff',
					backgroundColor: 'rgba(0, 0, 0, 0.85)',
					flexDirection: 'column',
					gap: '24px',
					padding: '20px',
				}}
			>
				{/* Анимированный спиннер с пульсацией */}
				<div
					style={{
						position: 'relative',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<CircularProgress
						size={80}
						thickness={3}
						className='pulse-animation'
						style={{
							color: '#2196f3',
						}}
					/>
					<div
						style={{
							position: 'absolute',
							fontSize: '14px',
							fontWeight: 600,
							color: '#fff',
						}}
					>
						{updateProgress.total > 0 ? Math.round((updateProgress.current / updateProgress.total) * 100) : 0}%
					</div>
				</div>
				<div
					style={{
						textAlign: 'center',
						fontSize: '20px',
						fontWeight: 600,
						color: '#fff',
						maxWidth: '450px',
						padding: '0 20px',
						marginBottom: '8px',
					}}
				>
					Обновляем регионы
				</div>
				<div
					style={{
						textAlign: 'center',
						fontSize: '16px',
						color: '#e3f2fd',
						maxWidth: '450px',
						padding: '0 20px',
						marginBottom: '4px',
						fontWeight: 500,
					}}
				>
					Обработано {updateProgress.current} из {updateProgress.total} расценок
				</div>
				<div
					style={{
						textAlign: 'center',
						fontSize: '14px',
						color: '#bbdefb',
						maxWidth: '450px',
						padding: '0 20px',
						opacity: 0.9,
					}}
				>
					{updateProgress.current === 0
						? 'Подготавливаем данные...'
						: updateProgress.current === updateProgress.total
						? 'Завершаем обновление...'
						: 'Пожалуйста, подождите. Не закрывайте страницу'}
				</div>
			</Backdrop>

			{/* Оверлей для блокировки интерфейса во время удаления расценок */}
			<Backdrop
				open={isDeletingRates}
				className='loading-overlay'
				style={{
					zIndex: 9999,
					color: '#fff',
					backgroundColor: 'rgba(0, 0, 0, 0.85)',
					flexDirection: 'column',
					gap: '24px',
					padding: '20px',
				}}
			>
				{/* Анимированный спиннер с пульсацией */}
				<div
					style={{
						position: 'relative',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<CircularProgress
						size={80}
						thickness={3}
						className='pulse-animation'
						style={{
							color: '#f44336',
						}}
					/>
					<div
						style={{
							position: 'absolute',
							fontSize: '14px',
							fontWeight: 600,
							color: '#fff',
						}}
					>
						{deleteProgress.total > 0 ? Math.round((deleteProgress.current / deleteProgress.total) * 100) : 0}%
					</div>
				</div>
				<div
					style={{
						textAlign: 'center',
						fontSize: '20px',
						fontWeight: 600,
						color: '#fff',
						maxWidth: '450px',
						padding: '0 20px',
						marginBottom: '8px',
					}}
				>
					Удаляем расценки
				</div>
				<div
					style={{
						textAlign: 'center',
						fontSize: '16px',
						color: '#ffcdd2',
						maxWidth: '450px',
						padding: '0 20px',
						marginBottom: '4px',
						fontWeight: 500,
					}}
				>
					Удалено {deleteProgress.current} из {deleteProgress.total} расценок
				</div>
				<div
					style={{
						textAlign: 'center',
						fontSize: '14px',
						color: '#ffab91',
						maxWidth: '450px',
						padding: '0 20px',
						opacity: 0.9,
					}}
				>
					{deleteProgress.current === 0
						? 'Подготавливаем удаление...'
						: deleteProgress.current === deleteProgress.total
						? 'Завершаем операцию...'
						: 'Пожалуйста, подождите. Не закрывайте страницу'}
				</div>
			</Backdrop>
		</>
	)
}

export { TableContainer }
